<script>
  import { format } from 'svelte-i18n';
  import { Card, CardBody } from '@sveltestrap/sveltestrap';

  import { ColorPickerField, SelectField, TextField } from '@client/components/fields/standalone';
  import { ScrollableSection } from '@client/components/scrollspy';
  import { ShowMore } from '@client/components/show-more';

  import { FONT_WEIGHTS } from '@client/enums';

  import { isValidUrl } from '@common/utils/validation';

  export let settings = {};
  export let onUpdate = null;
  export let categories = [];

  $: showActions = true; // Since this is a staff-only section
</script>

<div id="branding">
  <ScrollableSection id="branding">
    <h1 class="mb-3">{$format('label.GENERAL')}</h1>
    <Card>
      <CardBody class="p-4 pt-1">
        <TextField
          id="branding-companyname"
          label={$format('label.COMPANY_NAME')}
          tooltip={$format('label.TOOLTIP.COMPANY_NAME')}
          bind:value={settings.branding.companyname}
          {onUpdate}
          {showActions}
          allowCopy
        />

        <TextField
          id="branding-companyurl"
          label={$format('label.COMPANY_URL')}
          tooltip={$format('label.TOOLTIP.COMPANY_URL')}
          validationError={$format('label.VALIDATION_ERROR_NO_VALID_URL')}
          validation={isValidUrl}
          bind:value={settings.branding.companyurl}
          {onUpdate}
          {showActions}
          allowCopy
        />

        <TextField
          id="branding-logo"
          label={$format('label.LOGO_URL')}
          tooltip={$format('label.TOOLTIP.LOGO_URL')}
          validationError={$format('label.VALIDATION_ERROR_NO_VALID_URL')}
          validation={isValidUrl}
          bind:value={settings.branding.logo}
          {onUpdate}
          {showActions}
          allowCopy
        >
          {#if settings.branding?.logo}
            <ShowMore collapsedText={$format('label.SHOW_PREVIEW')} expandedText={$format('label.HIDE_PREVIEW')}>
              <div class="image-preview">
                <div class="bold">{$format('label.PREVIEW')}</div>
                <img src={settings.branding.logo} alt={$format('label.COMPANY_LOGO')} />
              </div>
            </ShowMore>
          {/if}
        </TextField>

        <TextField
          id="branding-icon"
          label={$format('label.ICON_URL')}
          tooltip={$format('label.TOOLTIP.LOGO_URL')}
          validationError={$format('label.VALIDATION_ERROR_NO_VALID_URL')}
          validation={isValidUrl}
          bind:value={settings.branding.icon}
          {onUpdate}
          {showActions}
          allowCopy
        >
          {#if settings.branding?.icon}
            <ShowMore collapsedText={$format('label.SHOW_PREVIEW')} expandedText={$format('label.HIDE_PREVIEW')}>
              <div class="image-preview">
                <div class="bold">{$format('label.PREVIEW')}</div>
                <img src={settings.branding.icon} alt={$format('label.COMPANY_LOGO')} />
              </div>
            </ShowMore>
          {/if}
        </TextField>

        <ColorPickerField
          id="branding-primary"
          label={$format('label.BRAND_PRIMARY_COLOR')}
          tooltip={$format('label.TOOLTIP.BRAND_PRIMARY_COLOR')}
          bind:value={settings.branding.primary}
          {onUpdate}
          {showActions}
        />

        <ColorPickerField
          id="branding-secondary"
          label={$format('label.BRAND_SECONDARY_COLOR')}
          tooltip={$format('label.TOOLTIP.BRAND_SECONDARY_COLOR')}
          bind:value={settings.branding.secondary}
          {onUpdate}
          {showActions}
        />

        <ColorPickerField
          id="branding-tertiary"
          label={$format('label.BRAND_TERTIARY_COLOR')}
          tooltip={$format('label.TOOLTIP.BRAND_TERTIARY_COLOR')}
          bind:value={settings.branding.tertiary}
          {onUpdate}
          {showActions}
        />

        <ColorPickerField
          id="branding-backgroundcolor"
          label={$format('label.BRAND_BACKGROUND_COLOR')}
          tooltip={$format('label.TOOLTIP.BRAND_BACKGROUND_COLOR')}
          bind:value={settings.branding.backgroundcolor}
          {onUpdate}
          {showActions}
        />

        <ColorPickerField
          id="branding-textcolor"
          label={$format('label.BRAND_TEXT_COLOR')}
          tooltip={$format('label.TOOLTIP.BRAND_TEXT_COLOR')}
          bind:value={settings.branding.textcolor}
          {onUpdate}
          {showActions}
        />

        <TextField
          id="branding-fontfamily"
          label={$format('label.BRAND_FONT_FAMILY')}
          tooltip={$format('label.TOOLTIP.BRAND_FONT_FAMILY')}
          bind:value={settings.branding.fontfamily}
          {onUpdate}
          {showActions}
        />

        <SelectField
          id="branding-fontweight"
          label={$format('label.BRAND_FONT_WEIGHT')}
          tooltip={$format('label.TOOLTIP.BRAND_FONT_WEIGHT')}
          bind:value={settings.branding.fontweight}
          selected={FONT_WEIGHTS.find(({ value }) => value === settings.branding.fontweight) || ''}
          options={FONT_WEIGHTS}
          {onUpdate}
          {showActions}
        />

        <TextField
          id="branding-headerfontfamily"
          label={$format('label.BRAND_HEADER_FONT_FAMILY')}
          tooltip={$format('label.TOOLTIP.BRAND_HEADER_FONT_FAMILY')}
          bind:value={settings.branding.headerfontfamily}
          {onUpdate}
          {showActions}
        />

        <SelectField
          id="branding-headerfontweight"
          label={$format('label.BRAND_HEADER_FONT_WEIGHT')}
          tooltip={$format('label.TOOLTIP.BRAND_HEADER_FONT_WEIGHT')}
          bind:value={settings.branding.headerfontweight}
          selected={FONT_WEIGHTS.find(({ value }) => value === settings.branding.headerfontweight) || ''}
          options={FONT_WEIGHTS}
          {onUpdate}
          {showActions}
        />
      </CardBody>
    </Card>
  </ScrollableSection>
</div>

<style lang="scss">
  .image-preview {
    margin-top: 1rem;

    .bold {
      font-weight: bold;
      margin-bottom: 0.5rem;
    }

    img {
      max-width: 200px;
      max-height: 100px;
      object-fit: contain;
      border: 1px solid #ddd;
      border-radius: 4px;
      padding: 0.5rem;
    }
  }
</style>
